import React, { useEffect } from 'react'
import ResponsiveImage from '@components/ResponsiveImage'

import { TeaserSliderAriaLabels } from '../TeaserSliderTypes'

interface TeaserSliderMainArticleProperties {
  image: { originalImageUrl: string; imageUrl: string; altText: string }
  formattedPublicationDate: string
  blogTags: string[]
  buttonLabel?: string
  title: string
  description: string
  pageLink: { externalizedURL?: string; isTargetBlank: boolean }
  ariaLabels?: TeaserSliderAriaLabels
}

const TeaserSliderMainArticle: React.FC<TeaserSliderMainArticleProperties> = ({
  image,
  formattedPublicationDate,
  blogTags,
  title,
  description,
  pageLink,
  buttonLabel,
  ariaLabels,
}: TeaserSliderMainArticleProperties) => {
  const teaserSliderMainItem = React.useRef<HTMLDivElement>(null)
  const [isSelected, setIsSelected] = React.useState(false)

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (teaserSliderMainItem.current) {
        setIsSelected(teaserSliderMainItem.current.classList.contains('is-selected'))
      }
    })

    if (teaserSliderMainItem.current) {
      observer.observe(teaserSliderMainItem.current, { attributes: true, attributeFilter: ['class'] })
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div ref={teaserSliderMainItem} className="cmp-teaser-slider__main-item">
      <div className="cmp-teaser-slider__main-image">
        <ResponsiveImage src={image.imageUrl || image.originalImageUrl} alt={image.altText} />
      </div>
      <div className="cmp-teaser-slider__main-content">
        <div className="cmp-teaser-slider__main-meta">
          <span className="sr-only">{ariaLabels?.dateLabel || 'Date: '}</span>
          <span className="cmp-teaser-slider__main-date">{formattedPublicationDate}</span>
          <div className="cmp-blog-tag">
            <span className="sr-only" id={`main-article-category-${title}`}>
              {ariaLabels?.tagLabel || 'Categories: '}
            </span>
            <ul className="cmp-blog-tag__list" aria-labelledby={`main-article-category-${title}`}>
              <li className="cmp-blog-tag__list-item">{blogTags[0]}</li>
              {blogTags.length > 1 ? <li className="cmp-blog-tag__list-item">{`+ ${blogTags.length - 1}`}</li> : null}
            </ul>
          </div>
        </div>
        <h3 className="cmp-teaser-slider__main-title">{title}</h3>
        <div className="cmp-teaser-slider__main-description">{description}</div>
        <a
          href={pageLink.externalizedURL}
          target={pageLink.isTargetBlank ? '_blank' : ''}
          rel={pageLink.isTargetBlank ? 'noopener noreferrer' : ''}
          className="cmp-teaser-slider__main-cta"
          tabIndex={isSelected ? 0 : -1}
        >
          {buttonLabel || 'Read more'}
        </a>
      </div>
    </div>
  )
}

export default TeaserSliderMainArticle
